import {
  useAccountQuery,
  useChargebeeCheckoutMutation,
  useTrackMutation
} from '@mote/api'
import { TickFilledIcon } from '@mote/assets/icons'
import { LEGALS_VERSION } from '@mote/helpers/constants'
import { useGoalsOnboarding } from '@mote/helpers/hooks/business'
import { snakify } from '@mote/helpers/misc'
import { Block, Button, Checkbox, H4, H5, P } from '@mote/ui'
import React from 'react'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useAuth } from 'web/src/auth/useAuth'
import { identifyMaybeAndBroadcast } from 'web/src/shared/Auth'
import { getGodModeOptions } from 'web/src/shared/Utils'
import { useOnboarding } from '../context'

const MAX_POLLS = 20
const POLL_SLEEP = 1000

export const UpsellContent = ({
  products,
  isMinor = false,
  isStudent = false,
  hasError,
  onError
}) => {
  const history = useHistory()
  const goalsState = useGoalsOnboarding()
  const { user, updateUser } = useAuth()
  const { mutate: track } = useTrackMutation()
  const { mutate: queryAccount } = useAccountQuery()
  const { updateProperties } = useOnboarding()
  const [consentAccept, setConsentAccept] = React.useState(false)
  const [newsletterAccept, setNewsletterAccept] = React.useState(false)
  const [selectedPlan, setSelectedPlan] = React.useState(null)
  const [isStateLoading, setIsStateLoading] = React.useState(false)
  const {
    mutate: createChargebeeCheckout,
    loading: isChargebeeCheckoutLoading
  } = useChargebeeCheckoutMutation()
  const plans = React.useMemo(() => {
    const unitPrice = products
      ? `${products.getUnitPrice(1)}/year`
      : 'Loading...'
    const defaultPlanTitle =
      isMinor || user?.productEnum === 'free'
        ? 'Stay on Free'
        : 'Unlimited Trial (30 days)'
    const defaultPlanPrice =
      isMinor || user?.productEnum === 'free'
        ? 'Limited features'
        : 'No card required'

    return [
      {
        key: 'default',
        title: defaultPlanTitle,
        price: defaultPlanPrice
      },
      {
        key: 'unlimited',
        title: 'Unlimited (1 year)',
        price: unitPrice
      }
    ]
  }, [products, isMinor, user?.productEnum])

  const handleCtaClick = React.useCallback(
    async (mode, polls = 0) => {
      if (!isMinor && !consentAccept) return
      if (hasError) onError?.(false)
      if (!isStateLoading) setIsStateLoading(true)

      if (!goalsState.onboardingComplete && !goalsState.userOnboardingSkipped) {
        const response = await queryAccount({
          variables: getGodModeOptions() || {}
        })

        if (response.error) {
          if (response?.error) console.log(response.error)
          onError?.(true)
          setIsStateLoading(false)
          return
        }

        const { goals } = response.data ?? {}

        if (goals?.userOnboarding !== 'completed') {
          if (polls < MAX_POLLS) {
            return setTimeout(() => handleCtaClick(mode, polls + 1), POLL_SLEEP)
          } else {
            onError?.(true)
            setIsStateLoading(false)
            return
          }
        }

        identifyMaybeAndBroadcast()
      }

      const response = await updateProperties({
        payload: {
          legals: LEGALS_VERSION,
          newslettersAndProductUpdatesOptIn: newsletterAccept
        },
        shouldUpdateUser: false
      })

      if (response.error) {
        onError?.(true)
        setIsStateLoading(false)
        return
      }

      if (
        goalsState.onboardingComplete ||
        goalsState.userOnboardingSkipped ||
        selectedPlan === 'default' ||
        mode === 'paid'
      ) {
        setIsStateLoading(false)
        updateUser(response.flattenedData)
        return history.push('/')
      }

      if (selectedPlan === 'unlimited' && mode === 'buying') {
        const productId = products?.getProductId()

        if (!productId) {
          setIsStateLoading(false)
          onError?.(true)
          return
        }

        const { data, error } = await createChargebeeCheckout({
          variables: {
            priceId: productId,
            quantity: 1,
            institutionMode: false,
            institutionData: {}
          }
        })

        if (error) {
          setIsStateLoading(false)
          onError?.(true)
          return
        }

        window.location.href = data.URL
      }
    },
    [
      consentAccept,
      newsletterAccept,
      selectedPlan,
      isMinor,
      hasError,
      isStateLoading
    ]
  )
  const buttonProps = React.useMemo(() => {
    const { isInstitutional, isPaid } = user ?? {}
    const paid = isInstitutional || isPaid
    const isTrial = !paid && selectedPlan === 'default'
    const mode = paid ? 'paid' : 'buying'
    let text = 'Continue to checkout'

    switch (true) {
      case goalsState.onboardingComplete || goalsState.userOnboardingSkipped:
        text = 'Accept and continue'
        break
      case user?.isInstitutionalAdmin:
        text = 'Continue to account page'
        break
      case paid ||
        (isTrial &&
          (isMinor || (!isMinor && isStudent) || user?.productEnum === 'free')):
        text = 'Complete account setup'
        break
      case isTrial && !(isMinor || user?.productEnum === 'free'):
        text = 'Start unlimited trial'
        break
      default:
    }

    const loadingText = text
    const onClick = async () => {
      if (
        !(goalsState.onboardingComplete || goalsState.userOnboardingSkipped)
      ) {
        setIsStateLoading(true)
        await track({
          variables: snakify({
            event: 'Goal Completed',
            goalName: 'userOnboarding'
          })
        })
      }
      handleCtaClick(mode)
    }

    return { text, loadingText, onClick }
  }, [selectedPlan, handleCtaClick, goalsState, isMinor, isStudent, user])
  const { title, subtitle } = React.useMemo(() => {
    let title = 'Choose your plan'
    let subtitle = `${
      isMinor || user?.productEnum === 'free'
        ? 'Stay on free plan'
        : 'Start with a 30 day trial of Mote Unlimited'
    }, or choose a yearly plan, from as little as ${
      products ? `${products.getUnitPrice(1)}/year` : ''
    }!`

    switch (true) {
      case goalsState.onboardingComplete || goalsState.userOnboardingSkipped:
        title = 'We’ve updated your mote experience'
        subtitle =
          'We require you to read and accept the Terms and Privacy Policy before continuing.'
        break
      case user?.isInstitutional || user?.isPaid || isStudent:
        title = 'Complete your account setup'
        subtitle =
          isMinor && (isStudent || (!user?.isInstitutional && user?.isPaid))
            ? "You're ready to go, click below to get started!"
            : 'Please read over the Terms and Privacy Policy. Once you’re happy, complete your account setup.'
        break
      default:
    }

    return { title, subtitle }
  }, [user, goalsState, products, isMinor, isStudent])

  React.useEffect(() => {
    if (!user || user?.isInstitutional || user?.isPaid) return

    setSelectedPlan('default')
  }, [])

  function handlePlanSelection(plan) {
    setSelectedPlan(plan)
  }

  function handleConsentAccept(accepted) {
    setConsentAccept(accepted)
  }

  function handleNewsletterAccept(accepted) {
    setNewsletterAccept(accepted)
  }

  return (
    <Root align='flex-start' justify='flex-start' gap={24} spreadX spreadY>
      <TitleContainer layout='vertical' align='flex-start' gap={16}>
        <Title fontSize={18} lineHeight='140%' fontWeight={700} color='#25252D'>
          {title}
        </Title>

        <Subtitle
          fontSize={15}
          lineHeight='140%'
          fontWeight='normal'
          color='#6C6C84'
        >
          {subtitle}
        </Subtitle>
      </TitleContainer>
      {!goalsState.onboardingComplete &&
        !goalsState.userOnboardingSkipped &&
        !(user?.isInstitutional || user?.isPaid || isStudent || isMinor) && (
          <PlansContainer
            layout='vertical'
            gap={16}
            disabled={isStateLoading || !products}
            spreadX
          >
            {plans.map(({ key, title, price }, index) => {
              const isSelected = key === selectedPlan

              return (
                <Plan
                  key={`${title}-${key}`}
                  title={title}
                  price={price}
                  selected={isSelected}
                  onClick={() => handlePlanSelection(key)}
                />
              )
            })}
          </PlansContainer>
        )}
      {(!isMinor || (user?.isInstitutional && isMinor)) && (
        <ConsentContainer layout='vertical' gap={16} disabled={isStateLoading}>
          <Checkbox
            checked={consentAccept}
            onChange={event => {
              const {
                target: { checked }
              } = event
              handleConsentAccept(checked)
            }}
            label={
              <>
                I agree to Mote’s{' '}
                <Button
                  tag='a'
                  variant='link'
                  size='sm'
                  href='/ToS'
                  text='Terms'
                />{' '}
                and{' '}
                <Button
                  tag='a'
                  variant='link'
                  size='sm'
                  href='/privacy'
                  text='Privacy Policy'
                />
              </>
            }
          />
          <Checkbox
            checked={newsletterAccept}
            onChange={event => {
              const {
                target: { checked }
              } = event
              handleNewsletterAccept(checked)
            }}
            label='Send me occasional updates about Mote features and content'
          />
        </ConsentContainer>
      )}
      <Button
        loading={isStateLoading || isChargebeeCheckoutLoading}
        disabled={!isMinor && !consentAccept}
        style={{ minWidth: '215px' }}
        {...buttonProps}
      />
    </Root>
  )
}

const Plan = ({ title, price, selected, onClick }) => {
  return (
    <PlanContainer
      layout='horizontal'
      align='center'
      gap={24}
      selected={selected}
      onClick={onClick}
    >
      <PlanCheckboxContainer
        align='center'
        justify='center'
        selected={selected}
      >
        <TickFilledIcon />
      </PlanCheckboxContainer>
      <PlanDetailsContainer layout='vertical' gap={4}>
        <PlanTitle
          fontSize={15}
          fontWeight={500}
          lineHeight='140%'
          color='#25252D'
        >
          {title}
        </PlanTitle>
        <PlanPrice
          fontSize={15}
          fontWeight={400}
          lineHeight='140%'
          color='#6C6C84'
        >
          {price}
        </PlanPrice>
      </PlanDetailsContainer>
    </PlanContainer>
  )
}

const Root = styled(Block)``

const TitleContainer = styled(Block)``
const Title = styled(H4)``
const Subtitle = styled(P)`
  max-width: 365px;
`

const PlansContainer = styled(Block)`
  transition: opacity 150ms ease-in;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
`

const PlanContainer = styled(Block)`
  padding: 24px;
  min-height: 100px;
  max-width: 430px;
  border-radius: 8px;
  border: 1.5px solid #d1d1e0;
  transition: all 150ms ease-in;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    cursor: pointer;
    border-color: #ac0ae8;
  }

  ${({ selected }) =>
    selected &&
    css`
      pointer-events: none;
      border-color: #ac0ae8;
    `};

  @media (max-width: 639px) {
    padding: 16px;
    gap: 12px;
  }
`

const PlanCheckboxContainer = styled(Block)`
  width: 32px;
  height: 32px;
  border-radius: 100%;
  border: 1px solid;
  flex-shrink: 0;
  transition: border 100ms ease-in;

  svg {
    height: 100%;
    width: 100%;
    transition-duration: 100ms;
    transition-timing-function: ease-in;
    transition-property: transform, opacity, visibility;
  }

  ${({ selected }) =>
    selected
      ? css`
          border-color: transparent;
          svg {
            opacity: 1;
            visibility: visible;
            transform: scale(1);
          }
        `
      : css`
          border-color: #d1d1e0;
          svg {
            opacity: 0;
            visibility: hidden;
            transform: scale(0);
          }
        `};

  @media (max-width: 639px) {
    width: 28px;
    height: 28px;
  }
`

const PlanDetailsContainer = styled(Block)``
const PlanTitle = styled(H5)``
const PlanPrice = styled(P)``

const ConsentContainer = styled(Block)`
  transition: opacity 150ms ease-in;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
`
